import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import { isGroupShape } from '@/lib/groups/group-kind'
import { computeLiveGroupWrapRect } from '@/lib/groups/shape-group-bounds'
import { collectLeafDescendants } from '@/lib/groups/shape-group-tree'
import { TEXT_BOX_PADDING } from './helpers/text-box-content-box'

import { DEFAULT_WRAP_OFFSET_PT } from '@/lib/object-effects/text-wrap'
import { computeShapeContourBandsBoundary } from '@/lib/boundaries/text-boundary'
import {
  type WrapTextBoxCandidate,
  rotatedAabb,
  shapeAffectsTextBox,
  textBoxAffectsTextBox,
} from '@/lib/text/wrap-obstacles'
import type { WrapBand } from '@/lib/text/types/wrap-band'
import { computeRotatedBoundingBoxBands, jumpBandFromRect } from './helpers/wrap-style-bands'

/**
 * Symmetric content padding used ONLY for wrap-band positioning — the real rendered CSS padding
 * is asymmetric (`VISUAL_PADDING_LEFT`/`TOP` on the left/top,
 * `CONTENT_PADDING_X`/`Y` on the right/bottom), a deliberate legacy-matching choice for where
 * glyphs start rendering ("flush top, 1px left"), unrelated to wrap. Using that same asymmetric
 * reference for wrap positioning made a perfectly centered object's left/top free space measure a
 * few points larger than its right/bottom — visible, unwanted asymmetry for the one thing (wrap)
 * that's supposed to treat all four sides identically. Averaging the two paddings and applying the
 * result equally to both sides of each axis keeps wrap bands symmetric while staying within ~1.5pt
 * of the real render on either side.
 *
 * Note what averaging does and doesn't cost: because `2 * WRAP_CONTENT_PADDING_X` is by
 * construction exactly `left + right`, the wrap content box has the SAME width and height as
 * the `TEXT_BOX_PADDING` content box — only its origin is shifted inward, by `(right - left) / 2` = 1.5px
 * horizontally and `(bottom - top) / 2` = 1px vertically. The dimensions are exact; the ~1.5pt
 * approximation above is purely that origin offset.
 */
export const WRAP_CONTENT_PADDING_X = (TEXT_BOX_PADDING.left + TEXT_BOX_PADDING.right) / 2
export const WRAP_CONTENT_PADDING_Y = (TEXT_BOX_PADDING.top + TEXT_BOX_PADDING.bottom) / 2

function rawBandsForTextBox(
  textBox: WrapTextBoxCandidate,
  shapes: ShapeData[],
  zIndexOf: (id: string) => number | undefined,
  otherTextBoxes: TextBoxData[] = []
): { bands: WrapBand[]; contentWidth: number; contentHeight: number } {
  // Symmetric on purpose — see WRAP_CONTENT_PADDING_X/Y above for why this deliberately doesn't
  // match the real (asymmetric) rendered CSS padding.
  const contentX = textBox.x + WRAP_CONTENT_PADDING_X
  const contentY = textBox.y + WRAP_CONTENT_PADDING_Y
  const contentWidth = Math.max(0, textBox.width - WRAP_CONTENT_PADDING_X * 2)
  const contentHeight = Math.max(0, textBox.height - WRAP_CONTENT_PADDING_Y * 2)

  // This text box opted out of every nearby wrap source (Object panel "Ignore wrap") — it lays
  // out exactly as if no wrap-eligible shape or text box were nearby, regardless of their own
  // wrapStyle. Bail before scanning sources at all.
  if (textBox.ignoreTextWrap === true) return { bands: [], contentWidth, contentHeight }

  if (contentWidth <= 0 || contentHeight <= 0) return { bands: [], contentWidth, contentHeight }

  const bands: WrapBand[] = []

  for (const shape of shapes) {
    // A group's boundary is its LIVE outer union of current children — never the group
    // ShapeData's own stored x/y/width/height, which only gets refreshed on entering/exiting
    // group isolation (`recomputePassiveGroupBounds`) and can be stale mid-edit. `rectShape` is
    // what every geometry check below uses; `shape` itself is only consulted for its `wrapStyle`/
    // offsets, which are unaffected by this substitution.
    const isGroup = isGroupShape(shape)
    const liveGroupRect = isGroup ? computeLiveGroupWrapRect(shapes, shape.id) : null
    const rectShape = liveGroupRect ? { ...shape, ...liveGroupRect } : shape

    if (!shapeAffectsTextBox(rectShape, textBox, zIndexOf)) continue

    // A group set to Wrap Shape decomposes into each of its LEAF members' own contour/rect,
    // independently — NOT the group's single outer bounding box (that stays exclusive to
    // Bounding Box/Jump, via the fallthrough below) — so text can flow into empty space
    // between/around individual members, anywhere inside the group's box that isn't actually
    // covered by a shape. Recurses through any nested group regardless of that nested group's
    // OWN style; every leaf is treated uniformly. The group's own `wrapOffsetTop` (its one
    // uniform contour/rect offset) governs every member — a member's own (dormant while
    // grouped) wrapStyle/offsets are not consulted here, matching AC3's "group wrap is
    // independent of children's wrap settings."
    if (isGroup && shape.wrapStyle === 'wrap') {
      const { shapes: memberShapes, textBoxes: memberTexts } = collectLeafDescendants(
        shapes,
        shape.id,
        otherTextBoxes
      )
      const groupOffset = shape.wrapOffsetTop ?? DEFAULT_WRAP_OFFSET_PT

      for (const member of memberShapes) {
        // parentGroupId cleared only on this probe copy, to pass shapeAffectsTextBox's ordinary
        // grouped-shape exclusion — the ancestor group (already confirmed active above) is what
        // authorizes this member as a source right now. wrapStyle forced to 'wrap' for the same
        // reason: the member's own (dormant) style is irrelevant while its group governs.
        const probe: ShapeData = { ...member, parentGroupId: undefined, wrapStyle: 'wrap' }

        if (!shapeAffectsTextBox(probe, textBox, zIndexOf)) continue
        bands.push(
          ...computeShapeContourBandsBoundary(
            member,
            groupOffset,
            contentX,
            contentY,
            contentWidth,
            contentHeight
          )
        )
      }

      for (const memberText of memberTexts) {
        // A text box has no contour beyond its own rect — same reasoning as textBoxAffectsTextBox
        // treating 'wrap' as geometrically identical to 'boundingBox' for a text box source.
        const probe: TextBoxData = {
          ...memberText,
          parentGroupId: undefined,
          wrapStyle: 'boundingBox',
        }

        if (!textBoxAffectsTextBox(probe, textBox, zIndexOf)) continue
        bands.push(
          ...computeRotatedBoundingBoxBands(
            probe,
            {
              wrapOffsetTop: groupOffset,
              wrapOffsetRight: groupOffset,
              wrapOffsetBottom: groupOffset,
              wrapOffsetLeft: groupOffset,
            },
            contentX,
            contentY,
            contentWidth,
            contentHeight
          )
        )
      }

      continue
    }

    if (shape.wrapStyle === 'wrap') {
      // Same "absent means the 6pt default, not 0" rule as bandFromRect below — Wrap Shape has
      // only one stored offset (`wrapOffsetTop`, the uniform contour offset), but it's still
      // subject to the same unset-vs-zero distinction.
      bands.push(
        ...computeShapeContourBandsBoundary(
          shape,
          shape.wrapOffsetTop ?? DEFAULT_WRAP_OFFSET_PT,
          contentX,
          contentY,
          contentWidth,
          contentHeight
        )
      )
      continue
    }

    if (shape.wrapStyle === 'jump') {
      // Full-width band over the shape's (rotated) vertical extent — see jumpBandFromRect.
      const band = jumpBandFromRect(
        rotatedAabb(rectShape),
        rectShape,
        contentY,
        contentWidth,
        contentHeight
      )

      if (band) bands.push(band)
      continue
    }

    // Bounding Box excludes the full rotated bounding box — the shape's own rect when unrotated,
    // otherwise the true rotated (and per-side offset, see `rotatedRectOffsetCorners`) quadrilateral
    // itself, scanline-intersected per row — so every side and corner of the rotated footprint
    // participates at any angle, never a bigger axis-aligned stand-in. For a group, this is the
    // Bounding Box/Jump path only — Wrap Shape decomposes into member obstacles above instead of
    // reaching here.
    bands.push(
      ...computeRotatedBoundingBoxBands(
        rectShape,
        rectShape,
        contentX,
        contentY,
        contentWidth,
        contentHeight
      )
    )
  }

  for (const source of otherTextBoxes) {
    if (!textBoxAffectsTextBox(source, textBox, zIndexOf)) continue

    if (source.wrapStyle === 'jump') {
      const band = jumpBandFromRect(
        rotatedAabb(source),
        source,
        contentY,
        contentWidth,
        contentHeight
      )

      if (band) bands.push(band)
      continue
    }

    bands.push(
      ...computeRotatedBoundingBoxBands(
        source,
        source,
        contentX,
        contentY,
        contentWidth,
        contentHeight
      )
    )
  }

  return { bands, contentWidth, contentHeight }
}

/**
 * Raw (not side-reduced) exclusion bands for every `wrapStyle: 'boundingBox'` shape or text box
 * affecting `textBox` — the input to `computeRowFreeSegments`/`layoutParagraphsWithBands`, which
 * support true both-sides wrap for a source sitting mid-paragraph. See `wrap-row-layout.ts`.
 */
export function computeWrapBands(
  textBox: WrapTextBoxCandidate,
  shapes: ShapeData[],
  zIndexOf: (id: string) => number | undefined,
  otherTextBoxes: TextBoxData[] = []
): WrapBand[] {
  return rawBandsForTextBox(textBox, shapes, zIndexOf, otherTextBoxes).bands
}
